// Contract API của platform-api (`/api/internal/v1`) — nguồn sự thật cho route, BFF và SDK (API05, K.7).
// Request + response đều `.strict()`: trường thừa ⇒ 422 (request) / lỗi contract (response).
import { IsoDateTime, list, success, Uuid } from "@secretaa/http-contract";
import { z } from "zod";

export const API_PREFIX = "/api/internal/v1";

const Email = z.string().trim().email().max(254);
const Password = z.string().min(10).max(128);
const DisplayName = z.string().trim().min(1).max(120);
export const Locale = z.enum(["vi", "en"]);
export const WorkspaceRole = z.enum(["owner", "admin", "member"]);
export const InviteRole = z.enum(["admin", "member"]);

// ---------- auth (chỉ BFF gọi, kèm service token) ----------
export const RegisterBody = z.object({ email: Email, password: Password, displayName: DisplayName, locale: Locale.default("vi") }).strict();
export const EmailBody = z.object({ email: Email }).strict();
export const TokenBody = z.object({ token: z.string().min(16).max(256) }).strict();
export const ResetPasswordBody = z.object({ token: z.string().min(16).max(256), newPassword: Password }).strict();
export const LoginBody = z.object({ email: Email, password: z.string().min(1).max(128) }).strict();
export const RefreshBody = z.object({ refreshToken: z.string().min(16).max(256) }).strict();
/** AUTH-BFF-01: BFF chỉ xin audience + workspace; KHÔNG có role / quyền. */
export const PlaneTokenBody = z.object({ workspaceId: Uuid, audience: z.literal("secretaa-data") }).strict();

export const SessionTokens = z
  .object({
    sessionId: Uuid,
    userId: Uuid,
    refreshToken: z.string(),
    refreshExpiresAt: IsoDateTime,
    accessToken: z.string(),
    accessExpiresAt: IsoDateTime,
  })
  .strict();
export const SessionTokensResponse = success(SessionTokens);
export const PlaneTokenResponse = success(z.object({ token: z.string(), expiresAt: IsoDateTime, scopeVersion: z.number().int() }).strict());
export const AcceptedResponse = success(z.object({ status: z.string() }).strict());

// ---------- người dùng / workspace ----------
export const User = z.object({ id: Uuid, email: z.string(), displayName: z.string(), locale: Locale, platformRole: z.enum(["owner"]).nullable() }).strict();
export const Membership = z.object({ workspaceId: Uuid, workspaceName: z.string(), role: WorkspaceRole, scopeVersion: z.number().int() }).strict();
export const MeResponse = success(z.object({ user: User, memberships: z.array(Membership) }).strict());

export const Workspace = z.object({ id: Uuid, name: z.string(), createdAt: IsoDateTime, updatedAt: IsoDateTime, version: z.number().int() }).strict();
export const CreateWorkspaceBody = z.object({ name: z.string().trim().min(1).max(120) }).strict();
export const UpdateWorkspaceBody = CreateWorkspaceBody;
export const WorkspaceResponse = success(z.object({ workspace: Workspace, myRole: WorkspaceRole, scopeVersion: z.number().int() }).strict());

export const Member = z
  .object({
    userId: Uuid,
    email: z.string(),
    displayName: z.string(),
    role: WorkspaceRole,
    orgScope: z.enum(["all", "restricted"]),
    unitIds: z.array(Uuid),
    joinedAt: IsoDateTime,
    version: z.number().int(),
  })
  .strict();
export const MembersResponse = success(z.array(Member));
export const UpdateMemberBody = z.object({ role: WorkspaceRole }).strict();
export const MemberScopeBody = z.object({ orgScope: z.enum(["all", "restricted"]), unitIds: z.array(Uuid).max(200) }).strict();
export const MemberResponse = success(Member);

export const Invitation = z
  .object({
    id: Uuid,
    email: z.string(),
    role: InviteRole,
    status: z.enum(["pending", "accepted", "revoked", "expired"]),
    expiresAt: IsoDateTime,
    createdAt: IsoDateTime,
  })
  .strict();
export const InviteBody = z.object({ email: Email, role: InviteRole }).strict();
export const InvitationResponse = success(Invitation);
export const InvitationsResponse = success(z.array(Invitation));
export const AcceptInvitationResponse = success(z.object({ workspaceId: Uuid }).strict());
/** Lời mời đang chờ cho email đã xác minh của người gọi (SAA-2026-011) — nhận theo id, không cần token. */
export const PendingInvitation = z
  .object({
    id: Uuid,
    workspaceId: Uuid,
    workspaceName: z.string(),
    inviterName: z.string(),
    role: InviteRole,
    expiresAt: IsoDateTime,
  })
  .strict();
export const PendingInvitationsResponse = success(z.array(PendingInvitation));
/** `details` của `INVITATION_EMAIL_MISMATCH`: email đang đăng nhập (của chính người gọi) + email được mời ĐÃ CHE. */
export const InvitationEmailMismatchDetails = z.object({ signedInEmail: z.string(), invitedEmail: z.string() }).strict();

// ---------- org units ----------
export const OrgUnit = z
  .object({ id: Uuid, parentId: Uuid.nullable(), type: z.string(), name: z.string(), archived: z.boolean(), createdAt: IsoDateTime, version: z.number().int() })
  .strict();
export const OrgUnitType = z.string().regex(/^[a-z][a-z0-9_]{1,31}$/);
export const CreateOrgUnitBody = z.object({ name: z.string().trim().min(1).max(120), type: OrgUnitType, parentId: Uuid.nullable().default(null) }).strict();
export const UpdateOrgUnitBody = z
  .object({ name: z.string().trim().min(1).max(120).optional(), parentId: Uuid.nullable().optional() })
  .strict()
  .refine((v) => v.name !== undefined || v.parentId !== undefined, "cần ít nhất một trường");
export const OrgUnitResponse = success(OrgUnit);
export const OrgUnitsResponse = success(z.array(OrgUnit));

// ---------- quyền cấp project (ADR-010) ----------
export const ProjectRole = z.enum(["viewer", "editor", "manager"]);
export const ProjectReservationResponse = success(z.object({ projectId: Uuid }).strict());
export const ProjectMember = z
  .object({
    userId: Uuid,
    email: z.string(),
    displayName: z.string(),
    role: ProjectRole,
    scopeIds: z.array(Uuid).nullable(),
    grantedBy: Uuid,
    updatedAt: IsoDateTime,
  })
  .strict();
export const ProjectMembersResponse = success(z.array(ProjectMember));
export const GrantProjectMemberBody = z.object({ role: ProjectRole, scopeIds: z.array(Uuid).min(1).max(50).nullable().default(null) }).strict();
export const ProjectGrantResponse = success(
  z.object({ projectId: Uuid, userId: Uuid, role: ProjectRole, scopeIds: z.array(Uuid).nullable(), updatedAt: IsoDateTime }).strict(),
);

/** Mọi grant project trong workspace — owner / admin (SAA-2026-012 D1.2). */
export const ProjectGrantRow = z.object({ projectId: Uuid, userId: Uuid, role: ProjectRole, grantedBy: Uuid, createdAt: IsoDateTime }).strict();
export const ProjectGrantsResponse = success(z.array(ProjectGrantRow));
/**
 * Đối soát grant mồ côi (BFF, SAA-2026-012 D1.2): grant trỏ tới project Data không có. `execute=false` (mặc định) chỉ báo;
 * `execute=true` thu hồi đúng các grant mồ côi. Project Data trả lỗi khác 404, hoặc grant mới hơn ngưỡng ⇒ `skipped`.
 */
export const ReconcileProjectGrantsBody = z.object({ execute: z.boolean().default(false) }).strict();
export const ReconcileProjectGrantsResponse = success(
  z
    .object({
      execute: z.boolean(),
      checkedProjects: z.number().int().nonnegative(),
      orphans: z.array(z.object({ projectId: Uuid, userIds: z.array(Uuid), oldestGrantAt: IsoDateTime }).strict()),
      skipped: z.array(z.object({ projectId: Uuid, reason: z.enum(["too_recent", "data_unavailable"]) }).strict()),
      revoked: z.number().int().nonnegative(),
    })
    .strict(),
);

// ---------- audit bảo mật ----------
export const SecurityAuditEvent = z
  .object({ id: Uuid, actorType: z.string(), actorId: z.string(), eventType: z.string(), resourceType: z.string(), resourceId: z.string().nullable(), createdAt: IsoDateTime })
  .strict();
export const SecurityAuditResponse = list(SecurityAuditEvent);

export const Jwks = z.object({ keys: z.array(z.record(z.string(), z.unknown())) });

export const PATHS = {
  register: "/auth/register",
  verifyEmail: "/auth/verify-email",
  resendVerification: "/auth/resend-verification",
  forgotPassword: "/auth/forgot-password",
  resetPassword: "/auth/reset-password",
  login: "/auth/login",
  refresh: "/auth/refresh",
  logout: "/auth/logout",
  planeToken: "/auth/plane-token",
  me: "/me",
  workspaces: "/workspaces",
  workspace: (id: string) => `/workspaces/${id}`,
  members: (id: string) => `/workspaces/${id}/members`,
  member: (id: string, userId: string) => `/workspaces/${id}/members/${userId}`,
  memberScope: (id: string, userId: string) => `/workspaces/${id}/members/${userId}/scope`,
  memberRemove: (id: string, userId: string) => `/workspaces/${id}/members/${userId}/remove`,
  invitations: (id: string) => `/workspaces/${id}/invitations`,
  invitationRevoke: (id: string, invId: string) => `/workspaces/${id}/invitations/${invId}/revoke`,
  acceptInvitation: "/invitations/accept",
  myInvitations: "/me/invitations",
  acceptInvitationById: (invId: string) => `/invitations/${invId}/accept`,
  orgUnits: (id: string) => `/workspaces/${id}/org-units`,
  orgUnit: (id: string, unitId: string) => `/workspaces/${id}/org-units/${unitId}`,
  orgUnitArchive: (id: string, unitId: string) => `/workspaces/${id}/org-units/${unitId}/archive`,
  securityAudit: (id: string) => `/workspaces/${id}/security-audit`,
  projectReservations: (id: string) => `/workspaces/${id}/project-reservations`,
  projectReservation: (id: string, projectId: string) => `/workspaces/${id}/project-reservations/${projectId}`,
  projectGrants: (id: string) => `/workspaces/${id}/project-grants`,
  projectMembers: (id: string, projectId: string) => `/workspaces/${id}/projects/${projectId}/members`,
  projectMember: (id: string, projectId: string, userId: string) => `/workspaces/${id}/projects/${projectId}/members/${userId}`,
} as const;
